import { runTurn, applyImportedScenario, createChatState } from '../core/agent.js'
import { generateContent, parseModelSelection, PROVIDER_LABELS } from '../core/llm.js'
import { validateScenario } from '../core/validation.js'
import type { Scenario } from '../core/scenario.js'
import { friendlyErrorMessage } from './errors.js'
import { renderScenarioPane, initScenarioPane } from './preview.js'
import { apiKeyFor, effectiveModel, hasAnyApiKey, openSettings, initSettings } from './settings.js'
import { initTheme } from './theme.js'
import {
  generateConversationId,
  listConversations,
  loadConversation,
  saveConversation,
  deleteConversation,
  type Conversation,
} from './store.js'

const chatMessages = document.getElementById('chat-messages') as HTMLDivElement
const chatForm = document.getElementById('chat-form') as HTMLFormElement
const chatInput = document.getElementById('chat-input') as HTMLTextAreaElement
const chatSend = document.getElementById('chat-send') as HTMLButtonElement
const newConversationBtn = document.getElementById('new-conversation-btn') as HTMLButtonElement
const conversationList = document.getElementById('conversation-list') as HTMLElement
const sidebar = document.getElementById('sidebar') as HTMLElement
const sidebarToggle = document.getElementById('sidebar-toggle') as HTMLButtonElement
const sidebarBackdrop = document.getElementById('sidebar-backdrop') as HTMLDivElement
const menuBtn = document.getElementById('menu-btn') as HTMLButtonElement
const importBtn = document.getElementById('import-btn') as HTMLButtonElement
const importFile = document.getElementById('import-file') as HTMLInputElement

const SIDEBAR_KEY = 'ttts-sidebar-collapsed'

// CSSのブレークポイント（style.cssの@mediaと一致させること）
const mobileLayout = window.matchMedia('(max-width: 860px)')
// ソフトウェアキーボード端末の判定。Enterは改行にして送信ボタンで送る
const touchInput = window.matchMedia('(hover: none) and (pointer: coarse)')

const SUGGESTIONS = [
  'ECサイトのログイン機能のテストシナリオを作って。正常系と異常系の両方',
  'https://example.com の問い合わせフォームのテスト。入力チェックも網羅して',
  '管理画面のユーザー登録・編集・削除のテストシナリオが欲しい',
]

function newDraft(): Conversation {
  return { id: '', title: '', createdAt: 0, updatedAt: 0, ...createChatState() }
}

let active: Conversation = newDraft()
let sending = false

// ---- chat rendering ----

function addMessage(role: 'user' | 'assistant' | 'system' | 'error', text: string): HTMLElement {
  chatMessages.querySelector('.chat-empty')?.remove()
  const div = document.createElement('div')
  div.className = `msg msg-${role}`
  div.textContent = text
  chatMessages.appendChild(div)
  chatMessages.scrollTop = chatMessages.scrollHeight
  return div
}

function addLoading(): HTMLElement {
  chatMessages.querySelector('.chat-empty')?.remove()
  const div = document.createElement('div')
  div.className = 'msg msg-assistant msg-loading'
  div.append('シナリオを考えています')
  const dots = document.createElement('span')
  dots.className = 'dots'
  div.appendChild(dots)
  chatMessages.appendChild(div)
  chatMessages.scrollTop = chatMessages.scrollHeight
  return div
}

function renderChatEmptyState(): void {
  const empty = document.createElement('div')
  empty.className = 'chat-empty'
  const p = document.createElement('p')
  p.textContent = '作りたいテストシナリオを自然な言葉で説明してください。'
  empty.appendChild(p)
  const wrap = document.createElement('div')
  wrap.className = 'chat-suggestions'
  for (const text of SUGGESTIONS) {
    const button = document.createElement('button')
    button.className = 'suggestion'
    button.textContent = text
    button.addEventListener('click', () => sendMessage(text))
    wrap.appendChild(button)
  }
  empty.appendChild(wrap)
  chatMessages.appendChild(empty)
}

function renderConversation(): void {
  chatMessages.innerHTML = ''
  if (active.transcript.length === 0) {
    renderChatEmptyState()
  } else {
    for (const entry of active.transcript) {
      addMessage(entry.role, entry.text)
    }
  }
  renderScenarioPane(active.scenario)
  renderSidebar()
}

// ---- persistence & routing ----

function deriveTitle(conv: Conversation): string {
  if (conv.scenario?.name) return conv.scenario.name
  const firstUser = conv.transcript.find((t) => t.role === 'user')
  if (firstUser) return firstUser.text.slice(0, 40)
  return '新しい会話'
}

/** 初回の操作時に会話を永続化対象にし、URLを /c/<id> に切り替える */
function ensurePersistent(conv: Conversation): void {
  if (conv.id) return
  conv.id = generateConversationId()
  conv.createdAt = Date.now()
  if (conv === active) {
    history.pushState(null, '', `/c/${conv.id}`)
  }
}

function persist(conv: Conversation): void {
  if (!conv.id) return
  conv.updatedAt = Date.now()
  conv.title = deriveTitle(conv)
  if (!saveConversation(conv)) {
    if (conv === active) {
      addMessage(
        'system',
        '保存容量が上限に達しました。サイドバーから古い会話を削除してください（この会話の内容は保存されていません）。',
      )
    }
  }
  renderSidebar()
}

function switchTo(conv: Conversation, options: { pushUrl: boolean }): void {
  active = conv
  if (options.pushUrl) {
    history.pushState(null, '', conv.id ? `/c/${conv.id}` : '/')
  }
  if (mobileLayout.matches) setSidebarCollapsed(true)
  renderConversation()
  if (!touchInput.matches) chatInput.focus()
}

function loadRoute(): void {
  const match = location.pathname.match(/^\/c\/([a-z0-9]+)$/)
  if (match) {
    const conv = loadConversation(match[1]!)
    if (conv) {
      active = conv
      renderConversation()
      return
    }
    history.replaceState(null, '', '/')
    active = newDraft()
    renderConversation()
    addMessage('system', '指定された会話が見つかりませんでした。新しい会話を開始します。')
    return
  }
  active = newDraft()
  renderConversation()
}

// ---- sidebar ----

function formatTime(timestamp: number): string {
  const diff = Date.now() - timestamp
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return 'たった今'
  if (minutes < 60) return `${minutes}分前`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}時間前`
  const date = new Date(timestamp)
  return `${date.getMonth() + 1}/${date.getDate()}`
}

function renderSidebar(): void {
  conversationList.innerHTML = ''
  const items = listConversations()

  if (items.length === 0) {
    const empty = document.createElement('div')
    empty.className = 'conv-empty'
    empty.textContent = '会話履歴はまだありません'
    conversationList.appendChild(empty)
    return
  }

  for (const meta of items) {
    const item = document.createElement('div')
    item.className = 'conv-item' + (meta.id === active.id ? ' active' : '')

    const open = document.createElement('button')
    open.className = 'conv-open'
    const title = document.createElement('span')
    title.className = 'conv-title'
    title.textContent = meta.title || '新しい会話'
    const time = document.createElement('span')
    time.className = 'conv-time'
    time.textContent = formatTime(meta.updatedAt)
    open.append(title, time)
    open.addEventListener('click', () => {
      if (meta.id === active.id) {
        if (mobileLayout.matches) setSidebarCollapsed(true)
        return
      }
      const conv = loadConversation(meta.id)
      if (conv) switchTo(conv, { pushUrl: true })
    })

    const del = document.createElement('button')
    del.className = 'conv-delete'
    del.title = 'この会話を削除'
    del.innerHTML =
      '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>'
    del.addEventListener('click', () => {
      if (!confirm(`「${meta.title || '新しい会話'}」を削除しますか？`)) return
      deleteConversation(meta.id)
      if (meta.id === active.id) {
        active = newDraft()
        history.replaceState(null, '', '/')
        renderConversation()
      } else {
        renderSidebar()
      }
    })

    item.append(open, del)
    conversationList.appendChild(item)
  }
}

/** サイドバーの開閉。モバイルではドロワーになるためバックドロップも連動させる */
function setSidebarCollapsed(collapsed: boolean): void {
  sidebar.classList.toggle('collapsed', collapsed)
  sidebarBackdrop.hidden = collapsed
}

function initSidebar(): void {
  setSidebarCollapsed(localStorage.getItem(SIDEBAR_KEY) === '1' || mobileLayout.matches)

  sidebarToggle.addEventListener('click', () => {
    const collapsed = !sidebar.classList.contains('collapsed')
    setSidebarCollapsed(collapsed)
    // モバイルのドロワー開閉は一時的な操作なので、デスクトップの折りたたみ設定を上書きしない
    if (!mobileLayout.matches) localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0')
  })

  menuBtn.addEventListener('click', () => setSidebarCollapsed(false))
  sidebarBackdrop.addEventListener('click', () => setSidebarCollapsed(true))

  // ウィンドウリサイズでレイアウトが切り替わったら、ドロワーの開きっぱなしを防ぐ
  mobileLayout.addEventListener('change', (e) => {
    if (e.matches) setSidebarCollapsed(true)
    else setSidebarCollapsed(localStorage.getItem(SIDEBAR_KEY) === '1')
  })

  newConversationBtn.addEventListener('click', () => {
    if (sending) return
    if (mobileLayout.matches) setSidebarCollapsed(true)
    if (!active.id && active.transcript.length === 0) {
      chatInput.focus()
      return
    }
    switchTo(newDraft(), { pushUrl: true })
  })
}

// ---- chat ----

async function sendMessage(text: string): Promise<void> {
  const trimmed = text.trim()
  if (sending || !trimmed) return

  let model
  try {
    model = parseModelSelection(effectiveModel())
  } catch (err) {
    const friendly = friendlyErrorMessage(err)
    addMessage('error', friendly.message)
    openSettings()
    return
  }

  const apiKey = apiKeyFor(model.provider)
  if (!apiKey) {
    addMessage(
      'system',
      `選択中のモデル（${model.modelId}）には ${PROVIDER_LABELS[model.provider]} のAPIキーが必要です。設定画面を開きます。`,
    )
    openSettings()
    return
  }

  sending = true
  chatInput.disabled = true
  chatSend.disabled = true

  const conv = active
  ensurePersistent(conv)

  addMessage('user', trimmed)
  const loading = addLoading()

  try {
    const result = await runTurn({ generate: generateContent }, conv, trimmed, {
      apiKey,
      model,
    })
    persist(conv)
    if (conv === active) {
      loading.remove()
      addMessage('assistant', result.reply)
      renderScenarioPane(conv.scenario)
    }
  } catch (err) {
    // runTurn はユーザー発言を transcript に積んでから失敗する可能性があるため保存する
    persist(conv)
    if (conv === active) {
      loading.remove()
      const friendly = friendlyErrorMessage(err)
      addMessage('error', friendly.message)
      if (friendly.needsSettings) openSettings()
    }
  } finally {
    sending = false
    chatInput.disabled = false
    chatSend.disabled = false
    if (conv === active) chatInput.focus()
  }
}

function initChat(): void {
  // 入力内容に合わせて高さを自動調整する（1行=送信ボタンと同じ高さ、上限はCSSのmax-height）
  const autoGrow = (): void => {
    chatInput.style.height = 'auto'
    chatInput.style.height = `${chatInput.scrollHeight}px`
  }
  chatInput.addEventListener('input', autoGrow)

  chatForm.addEventListener('submit', (e) => {
    e.preventDefault()
    const text = chatInput.value
    chatInput.value = ''
    chatInput.style.height = ''
    void sendMessage(text)
  })

  chatInput.addEventListener('keydown', (e) => {
    // ソフトウェアキーボードではEnterは改行として扱い、送信は送信ボタンで行う
    if (touchInput.matches) return
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault()
      chatForm.requestSubmit()
    }
  })
}

// ---- import ----

function initImport(): void {
  importBtn.addEventListener('click', () => importFile.click())

  importFile.addEventListener('change', async () => {
    const file = importFile.files?.[0]
    importFile.value = ''
    if (!file) return

    let scenario: Scenario
    try {
      scenario = JSON.parse(await file.text()) as Scenario
    } catch {
      addMessage('error', 'JSONの形式が不正です')
      return
    }

    const validation = validateScenario(scenario)
    if (!validation.valid) {
      addMessage(
        'error',
        `インポートしたJSONにエラーがあります:\n` +
          validation.errors.map((e) => `・${e.path}: ${e.message}`).join('\n'),
      )
      return
    }

    const conv = active
    ensurePersistent(conv)
    applyImportedScenario(conv, scenario)
    persist(conv)
    renderConversation()
    addMessage('system', '編集内容をチャットで指示してください。')
  })
}

// ---- boot ----

initTheme()
initSettings({
  onSaved: () => {
    try {
      const model = parseModelSelection(effectiveModel())
      if (!apiKeyFor(model.provider)) {
        addMessage(
          'system',
          `選択中のモデルを使うには ${PROVIDER_LABELS[model.provider]} のAPIキーが必要です。`,
        )
      }
    } catch {
      // モデルIDが不正な場合は送信時にエラー表示される
    }
  },
})
initScenarioPane(() => active.scenario)
initSidebar()
initChat()
initImport()

window.addEventListener('popstate', loadRoute)
loadRoute()

if (!hasAnyApiKey()) {
  openSettings()
} else if (!touchInput.matches) {
  // タッチ端末では自動フォーカスするとキーボードが開いてしまうため避ける
  chatInput.focus()
}
